import './ControlBarDisplay.css'

interface TimingSnapshot {
  simTime: number
  realTime: number
  fps: number
  realtimeRatio: number
}

interface ControlBarDisplayProps {
  controlBarPosition: { x: number; y: number; z: number }
  controlBarRotation: { roll: number; pitch: number; yaw: number }
  cameraPosition?: { x: number; y: number; z: number }
  cameraRotation?: { roll: number; pitch: number; yaw: number }
  timing?: TimingSnapshot | null
  /** Show mode: current dialogue line (character + text) for subtitle */
  showDialogue?: { characterId: string; text: string } | null
  /** Show title when in show mode */
  showTitle?: string | null
  /** Optional: resolve character id to display name */
  showCharacterName?: (id: string) => string
  /** When truth pose is active: mean distance (m) from puppet to truth across 8 attachments */
  meanErrorToTruth?: number | null
  /** Gravity scale vs 9.81 m/s² (1 = 1g, 0.15 = 0.15g, ~0.17 = ⅙g) */
  gravityScale?: number
}

export default function ControlBarDisplay({ 
  controlBarPosition, 
  controlBarRotation,
  cameraPosition,
  cameraRotation,
  timing,
  showDialogue,
  showTitle,
  showCharacterName,
  meanErrorToTruth,
  gravityScale
}: ControlBarDisplayProps) {
  // Convert radians to degrees for display
  const controlRollDeg = (controlBarRotation.roll * 180 / Math.PI).toFixed(1)
  const controlPitchDeg = (controlBarRotation.pitch * 180 / Math.PI).toFixed(1)
  const controlYawDeg = (controlBarRotation.yaw * 180 / Math.PI).toFixed(1)

  const cameraRollDeg = cameraRotation ? (cameraRotation.roll * 180 / Math.PI).toFixed(1) : '0.0'
  const cameraPitchDeg = cameraRotation ? (cameraRotation.pitch * 180 / Math.PI).toFixed(1) : '0.0'
  const cameraYawDeg = cameraRotation ? (cameraRotation.yaw * 180 / Math.PI).toFixed(1) : '0.0'

  return (
    <div className="control-bar-display">
      {/* Show dialogue / subtitle */}
      {(showTitle || showDialogue) && (
        <div className="display-group show-dialogue" aria-live="polite">
          {showTitle && <div className="show-title">{showTitle}</div>}
          {showDialogue && (
            <div className="show-line">
              <span className="show-character">
                {showCharacterName ? showCharacterName(showDialogue.characterId) : showDialogue.characterId}
              </span>
              <span className="show-text">{showDialogue.text}</span>
            </div>
          )}
        </div>
      )}
      {meanErrorToTruth != null && (
        <div className="display-group" aria-label="Pose error to truth">
          <span className="label">Mean error to truth:</span>
          <span className="value">{meanErrorToTruth.toFixed(3)} m</span>
        </div>
      )}
      {/* Time telemetry (always visible so users see Sim time, Real time, FPS, Realtime %) */}
      <div className="display-group" data-telemetry="time" aria-label="Time telemetry">
        <span className="label" style={{ marginRight: 8 }}>Time</span>
        <div className="display-section">
          <span className="label">Sim time:</span>
          <span className="value">{timing ? `${timing.simTime.toFixed(2)}s` : '—'}</span>
        </div>
        <div className="display-section">
          <span className="label">Real time:</span>
          <span className="value">{timing ? `${timing.realTime.toFixed(2)}s` : '—'}</span>
        </div>
        <div className="display-section">
          <span className="label">FPS:</span>
          <span className="value">{timing != null ? Math.round(timing.fps) : '—'}</span>
        </div>
        <div className="display-section">
          <span className="label">Realtime:</span>
          <span className="value" title="Sim time / real time; 100% = realtime, &gt;100% = faster, &lt;100% = slower">
            {timing != null ? `${(timing.realtimeRatio * 100).toFixed(0)}%` : '—'}
          </span>
        </div>
        {gravityScale != null && (
          <div className="display-section">
            <span className="label">G:</span>
            <span className="value" title="Gravity scale (1 = 9.81 m/s²)">
              {gravityScale === 1 ? '1g' : Math.abs(gravityScale - 1 / 6) < 0.01 ? '⅙g' : `${parseFloat(gravityScale.toFixed(2))}g`}
            </span>
          </div>
        )}
      </div>

      {/* Control Bar Info */}
      <div className="display-group">
        <div className="display-section">
          <span className="label">Control Bar Position:</span>
          <span className="value">X: {controlBarPosition.x.toFixed(2)}</span>
          <span className="value">Y: {controlBarPosition.y.toFixed(2)}</span>
          <span className="value">Z: {controlBarPosition.z.toFixed(2)}</span>
        </div>
        <div className="display-section">
          <span className="label">Control Bar Rotation:</span>
          <span className="value">Roll: {controlRollDeg}°</span>
          <span className="value">Pitch: {controlPitchDeg}°</span>
          <span className="value">Yaw: {controlYawDeg}°</span>
        </div>
      </div>
      
      {/* Camera Info */}
      {cameraPosition && cameraRotation && (
        <div className="display-group">
          <div className="display-section">
            <span className="label">Camera Position:</span>
            <span className="value">X: {cameraPosition.x.toFixed(2)}</span>
            <span className="value">Y: {cameraPosition.y.toFixed(2)}</span>
            <span className="value">Z: {cameraPosition.z.toFixed(2)}</span>
          </div>
          <div className="display-section">
            <span className="label">Camera Rotation:</span>
            <span className="value">Roll: {cameraRollDeg}°</span>
            <span className="value">Pitch: {cameraPitchDeg}°</span>
            <span className="value">Yaw: {cameraYawDeg}°</span>
          </div>
        </div>
      )}
    </div>
  )
}
